import { type WorkoutRepository } from '../application/workout.repository';
import {
  bumpAttempt,
  bumpSessionOpAttempt,
  deletePending,
  deletePendingSessionOp,
  listPending,
  listPendingSessionOps,
} from './dexie-queue';

/**
 * Single-flight guard: if a flush is already running, subsequent calls become
 * no-ops. Avoids stampedes when both `online` event and an interval fire.
 */
let inFlight: Promise<void> | null = null;

/**
 * Flush every queued set update through the repository, oldest first.
 * Stops on the first failure that isn't the queue itself running out of items
 * — leaves the rest queued for the next attempt.
 */
export function flushSetUpdates(repo: WorkoutRepository): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      // Set updates first — most volume, oldest-first. Stop on first hard
      // failure so the queue drains in order without scrambling timestamps.
      const pending = await listPending();
      for (const op of pending) {
        try {
          await repo.updateSet(op.setId, op.patch);
          if (op.id !== undefined) await deletePending(op.id);
        } catch (e) {
          if (op.id !== undefined) {
            const kept = await bumpAttempt(op.id, e);
            if (!kept) continue; // give-up: drop and move on
          }
          break;
        }
      }

      // Session-level ops: finish / cancel queued during a network drop.
      // Independent loop so a stuck set update can't block a workout from
      // closing once the user retries.
      const sessionOps = await listPendingSessionOps();
      for (const op of sessionOps) {
        try {
          if (op.kind === 'finish') {
            await repo.finish(op.sessionId, op.completedAt ?? undefined);
          } else {
            await repo.cancel(op.sessionId);
          }
          if (op.id !== undefined) await deletePendingSessionOp(op.id);
        } catch (e) {
          // Treat "already gone" / 404-style errors on cancel as success —
          // the user's intent was "delete", and a missing row matches that.
          if (op.kind === 'cancel' && isNotFoundError(e)) {
            if (op.id !== undefined) await deletePendingSessionOp(op.id);
            continue;
          }
          if (op.id !== undefined) {
            const kept = await bumpSessionOpAttempt(op.id, e);
            if (!kept) continue;
          }
          break;
        }
      }
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

function isNotFoundError(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  const msg = e.message.toLowerCase();
  return msg.includes('not found') || msg.includes('no rows') || msg.includes('404');
}

/**
 * Browser network errors come in many forms across fetch / supabase-js. We
 * use a small heuristic: TypeError from fetch, "Failed to fetch", or any
 * mention of network / offline / load failed.
 */
export function isLikelyNetworkError(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  if (!(error instanceof Error)) return false;
  if (error.name === 'TypeError') return true;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to fetch') ||
    msg.includes('network') ||
    msg.includes('load failed') ||
    msg.includes('fetch failed')
  );
}
